import React, { useContext, useMemo, useEffect } from 'react';
import { DomainContext } from '@schamburg-web/components';
import { Box, Typography, useTheme, Paper, useMediaQuery } from '@mui/material';

import {
  APIProvider,
  MapProps,
  Map,
  MapControl,
  ControlPosition,
  useMap,
} from '@vis.gl/react-google-maps';
import type { TerraDraw } from 'terra-draw';
import { UndoRedoControl } from './UndoRedoControl';
import { DrawingModeContext } from './DrawingModeContext';
import './NotificationMap.module.css';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { NotificationProfile } from '../../../types';
import { StepperComponentProps } from '../../../NewAccountStepper/types';
import { useBaseStepperContext } from '../../contexts/BaseStepperContext';
import { stepperStyles, stepperColors } from '../../styles';
import TerraDrawLayer from './TerraDrawLayer';

const mapOptions = {
  defaultCenter: { lat: 42.248, lng: -87.94384 },
  defaultZoom: 10,
  minZoom: 9,
  maxZoom: 16,
  disableDefaultUI: true,
} as MapProps;

const defaultMapOptions = {
  style: {
    width: '100%',
    height: '100%',
    minHeight: '400px',
  },
  gestureHandling: 'greedy',
  disableDefaultUI: true,
  mapId: '',
} as MapProps;

const NotificationMap: React.FC<StepperComponentProps> = ({
  description,
  stepIndex,
  label,
}) => {
  const { user, selectedProfileIndex, setError } = useLoginContext();
  const { updateStepValidation } = useBaseStepperContext();
  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const theme = useTheme();

  // Use stepIndex prop or default to 3 for backward compatibility
  const validationStepIndex = stepIndex !== undefined ? stepIndex : 3;

  const getMissingFields = React.useCallback(() => {
    if (
      !selectedProfile?.extRegions ||
      selectedProfile.extRegions.length === 0
    ) {
      return ['At least one notification region'];
    }
    if (selectedProfile.extRegions.length > 15) {
      return ['No more than 15 regions allowed'];
    }
    return [];
  }, [selectedProfile?.extRegions]);

  const missingFields = React.useMemo(
    () => getMissingFields(),
    [getMissingFields]
  );
  const hasErrors = React.useMemo(
    () => missingFields.length > 0,
    [missingFields]
  );

  const warningMessage = React.useMemo(() => {
    if (missingFields.length === 0) return '';
    if (missingFields.includes('No more than 15 regions allowed')) {
      return 'You can create up to 15 regions maximum';
    }
    return 'Please create at least one notification region';
  }, [missingFields]);

  // Update step validation whenever validation state changes
  useEffect(() => {
    updateStepValidation(validationStepIndex, warningMessage, hasErrors);
  }, [updateStepValidation, hasErrors, warningMessage, validationStepIndex]);

  // Set initial validation state based on existing regions
  useEffect(() => {
    if (selectedProfile?.extRegions) {
      const regionCount = selectedProfile.extRegions.length;
      if (regionCount > 0 && regionCount < 16) {
        setError(false);
      } else {
        setError(true);
      }
    } else {
      setError(true);
    }
  }, [selectedProfile?.extRegions, setError]);

  const domainContextVal = useContext(DomainContext);
  theme.palette.mode === 'dark'
    ? (defaultMapOptions.mapId = import.meta.env.VITE_MAP_ID_DARK)
    : (defaultMapOptions.mapId = import.meta.env.VITE_MAP_ID_LIGHT);

  const apiKey = useMemo(() => {
    return domainContextVal.domains.find((d) => d.name === 'extWebsite.apikey')
      ?.value;
  }, [domainContextVal]);

  // Responsive: show instructions before map on mobile, side-by-side on desktop
  // Use MUI's useMediaQuery for breakpoint detection
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const InstructionsPanel = (
    <Box sx={stepperStyles.sidebarPanel}>
      <Paper
        elevation={3}
        sx={{
          ...stepperStyles.elevatedCard(theme),
          textAlign: 'left',
          ...stepperStyles.autoHeight,
          ...stepperStyles.sticky(),
        }}
      >
        <Typography variant="h6" sx={stepperStyles.sectionTitle(theme)}>
          Adding Areas
        </Typography>
        <Box sx={stepperStyles.flexColumnGap(3)}>
          <Box sx={stepperStyles.instructionStep}>
            <Box sx={stepperStyles.numberedBadge(stepperColors.primary(theme))}>
              1
            </Box>
            <Typography
              variant="body2"
              sx={stepperStyles.instructionText(theme)}
            >
              Click the rectangle tool in the map controls to activate drawing
              mode
            </Typography>
          </Box>
          <Box sx={stepperStyles.instructionStep}>
            <Box sx={stepperStyles.numberedBadge(stepperColors.primary(theme))}>
              2
            </Box>
            <Typography
              variant="body2"
              sx={stepperStyles.instructionText(theme)}
            >
              Click and drag on the map to create a rectangular notification
              area
            </Typography>
          </Box>
          <Box sx={stepperStyles.instructionStep}>
            <Box
              sx={stepperStyles.numberedBadge(stepperColors.secondary(theme))}
            >
              3
            </Box>
            <Typography
              variant="body2"
              sx={stepperStyles.instructionText(theme)}
            >
              Drag the corners of existing regions to resize them
            </Typography>
          </Box>
          <Box sx={stepperStyles.instructionStep}>
            <Box sx={stepperStyles.numberedBadge(stepperColors.error(theme))}>
              ×
            </Box>
            <Typography
              variant="body2"
              sx={stepperStyles.instructionText(theme)}
            >
              Click on an existing region to delete it
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const MapPanel = (
    <Box sx={stepperStyles.mainContentPanel}>
      <Paper
        elevation={3}
        sx={{
          ...stepperStyles.paperCard(theme, 3),
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box sx={stepperStyles.mapContainer}>
          {apiKey && (
            <APIProvider
              apiKey={apiKey}
              key={`provider-${selectedProfileIndex}`}
            >
              <MapContainer
                selectedProfileIndex={selectedProfileIndex}
                selectedProfile={selectedProfile}
              />
            </APIProvider>
          )}
          {!apiKey && (
            <Box
              sx={{
                ...stepperStyles.flexCenter,
                ...stepperStyles.errorContainer(theme),
              }}
            >
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography
                  variant="h6"
                  color="error"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Map Service Unavailable
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Google Maps API key not found. Please contact your
                  administrator.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          {description}
        </Typography>
        {/* Status Card */}
        <Paper
          elevation={2}
          sx={{
            ...stepperStyles.statusCard(
              theme,
              selectedProfile?.extRegions?.length > 0
            ),
            p: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={stepperStyles.statusIndicator(
                selectedProfile?.extRegions?.length > 0
                  ? stepperColors.success(theme)
                  : stepperColors.warning(theme)
              )}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0,
                }}
              >
                {selectedProfile?.extRegions?.length ?? 0} region
                {(selectedProfile?.extRegions?.length ?? 0) === 1
                  ? ''
                  : 's'}{' '}
                defined
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                (1-15 required)
              </Typography>
            </Box>
          </Box>
        </Paper>
        {/* Responsive layout: instructions before map on mobile, side-by-side on desktop */}
        <Box sx={stepperStyles.responsiveLayout}>
          {isMobile ? (
            <>
              {InstructionsPanel}
              {MapPanel}
            </>
          ) : (
            <>
              {InstructionsPanel}
              {MapPanel}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const MapContainer: React.FC<{
  selectedProfileIndex?: number;
  selectedProfile?: NotificationProfile;
}> = ({ selectedProfileIndex, selectedProfile }) => {
  const [drawingMode, setDrawingMode] = React.useState<'select' | 'rectangle'>(
    'select'
  );

  return (
    <Box sx={{ flexGrow: 1, borderRadius: 2, overflow: 'hidden' }}>
      {/* TerraDrawLayer initializes the draw instance once the map is ready. */}
      <TerraDrawLayer>
        {(draw) => (
          <DrawingModeContext.Provider value={{ drawingMode, setDrawingMode }}>
            <Map
              key={`map-${selectedProfileIndex}`}
              {...{
                ...defaultMapOptions,
                ...mapOptions,
              }}
            />
            {/* MapControl renders a simple toolbar inside the map UI. */}
            <MapControl position={ControlPosition.TOP_CENTER}>
              <div className="terra-draw-toolbar">
                <MyMapControl
                  key={`control-${selectedProfileIndex}`}
                  draw={draw}
                  onDrawingModeChange={setDrawingMode}
                />
              </div>
            </MapControl>
          </DrawingModeContext.Provider>
        )}
      </TerraDrawLayer>
    </Box>
  );
};

const MyMapControl = ({
  draw,
  onDrawingModeChange,
}: {
  draw: TerraDraw | null;
  onDrawingModeChange: (mode: 'select' | 'rectangle') => void;
}) => {
  const map = useMap();

  // Trigger map resize when it's available and when container size changes
  useEffect(() => {
    if (map) {
      // Immediate resize
      const triggerResize = () => {
        google.maps.event.trigger(map, 'resize');
      };

      // Initial resize with delay
      const timer = setTimeout(triggerResize, 100);

      // Set up ResizeObserver to handle container size changes
      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        const mapDiv = map.getDiv();
        if (mapDiv?.parentElement) {
          resizeObserver = new ResizeObserver(() => {
            triggerResize();
          });
          resizeObserver.observe(mapDiv.parentElement);
        }
      }

      return () => {
        clearTimeout(timer);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [map]);

  return (
    <UndoRedoControl draw={draw} onDrawingModeChange={onDrawingModeChange} />
  );
};

export default NotificationMap;
